import { useCallback, useEffect, useState } from 'react';
import type { ImportResult, ProcessedReport } from '@integratio/core';
import { AppHeader, type FlowStep } from './components/AppHeader';
import { ProcessingStatus } from './components/ProcessingStatus';
import { ReportDashboard } from './components/ReportDashboard';
import { HomeScreen } from './screens/HomeScreen';
import { LoginScreen } from './screens/LoginScreen';
import { ReviewScreen } from './screens/ReviewScreen';
import { ApiError, api } from './lib/api';
import { useBranding } from './branding/BrandingProvider';

export function App(): JSX.Element {
  const branding = useBranding();
  const [step, setStep] = useState<FlowStep>('upload');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [report, setReport] = useState<ProcessedReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authUser, setAuthUser] = useState<string | null>(null);

  const describe = (cause: unknown, fallback: string): string =>
    cause instanceof ApiError ? cause.message : fallback;

  useEffect(() => {
    let active = true;
    void api
      .getSession()
      .then((session) => {
        if (!active) return;
        if (session.authenticated) {
          setAuthenticated(true);
          setAuthUser(session.username ?? 'integratio');
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setAuthReady(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleLogin = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const session = await api.login(username.trim(), password);
      setAuthenticated(true);
      setAuthUser(session.username ?? username.trim());
      setPassword('');
    } catch (cause) {
      setError(describe(cause, 'Não foi possível autenticar agora.'));
    } finally {
      setBusy(false);
    }
  }, [password, username]);

  const handleLogout = useCallback(async () => {
    setBusy(true);
    try {
      await api.logout();
    } catch {
      // ignora falha de rede no logout e limpa o estado local
    } finally {
      setAuthenticated(false);
      setAuthUser(null);
      setImportResult(null);
      setReport(null);
      setError(null);
      setStep('upload');
      setBusy(false);
    }
  }, []);

  const handleUpload = useCallback(async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.uploadSpreadsheet(file);
      setImportResult(result);
      setStep('review');
    } catch (cause) {
      setError(describe(cause, 'Não foi possível ler a planilha. Verifique o arquivo e tente novamente.'));
    } finally {
      setBusy(false);
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!importResult) return;
    setBusy(true);
    setError(null);
    setStep('processing');
    try {
      // Pequeno atraso perceptível apenas para exibir o progresso das etapas.
      const [processed] = await Promise.all([
        api.generateReport(importResult.importId),
        new Promise((resolve) => setTimeout(resolve, 1600)),
      ]);
      setReport(processed);
      setStep('dashboard');
    } catch (cause) {
      setError(describe(cause, 'Não foi possível processar os dados confirmados.'));
      setStep('review');
    } finally {
      setBusy(false);
    }
  }, [importResult]);

  const handleRestart = useCallback(() => {
    setImportResult(null);
    setReport(null);
    setError(null);
    setStep('upload');
  }, []);

  const handleOpenReport = useCallback(async (reportId: string) => {
    setBusy(true);
    setError(null);
    try {
      const processed = await api.getReport(reportId);
      setReport(processed);
      setStep('dashboard');
    } catch (cause) {
      setError(describe(cause, 'Não foi possível abrir o relatório.'));
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <AppHeader current={step} username={authenticated ? authUser ?? undefined : undefined} onLogout={authenticated ? () => void handleLogout() : undefined} />

      <main className="flex-1">
        {!authReady ? (
          <div className="mx-auto max-w-[1400px] px-6 py-16 text-sm text-brand-slate">Validando sessão…</div>
        ) : !authenticated ? (
          <LoginScreen
            busy={busy}
            error={error}
            username={username}
            password={password}
            onUsernameChange={setUsername}
            onPasswordChange={setPassword}
            onSubmit={() => void handleLogin()}
          />
        ) : (
          <>
        {step === 'upload' && (
          <HomeScreen
            onUpload={(file) => void handleUpload(file)}
            busy={busy}
            error={error}
            onOpenReport={(reportId) => void handleOpenReport(reportId)}
          />
        )}

        {step === 'review' && importResult && (
          <ReviewScreen
            result={importResult}
            onConfirm={() => void handleConfirm()}
            onCancel={handleRestart}
            busy={busy}
            error={error}
          />
        )}

        {step === 'processing' && importResult && (
          <div className="mx-auto max-w-[1400px] px-6 py-16">
            <ProcessingStatus fileName={importResult.fileName} />
          </div>
        )}

        {step === 'dashboard' && report && (
          <div className="mx-auto max-w-[1400px] px-6 py-10">
            <ReportDashboard report={report} onRestart={handleRestart} />
          </div>
        )}
          </>
        )}
      </main>

      <footer className="border-t border-brand-line bg-white py-5">
        <p className="mx-auto max-w-[1400px] px-6 text-xs text-brand-slate">{branding.footer}</p>
      </footer>
    </div>
  );
}
